/**
 * ObjectivesWorkflowConfigModal Component
 *
 * Modal for editing objectives configuration from the pre-trading workflow screen.
 * Only handles maxObjectives (which is snapshotted in draft config).
 * Includes "Apply to future sessions" checkbox to optionally update global settings.
 *
 * Note: pinObjectivesToLive is NOT handled here - it's a global-only UX preference
 * for the live dashboard (not yet designed). Managed in Settings/ObjectivesConfigModal.
 *
 * Key behaviors:
 * - Manual Save/Cancel (not auto-save) - config changes are significant
 * - Checkbox always visible; default checked if draft matched global at open, unchecked if divergent
 * - "Reset to defaults" button visible when draft was divergent at modal open
 * - Alert shown when draft was divergent at modal open (not triggered by active edits)
 * - Saves to draft config first, optionally to global settings if checkbox checked
 *
 * Named differently from Settings/ObjectivesConfigModal to avoid import confusion.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Link,
  NumberInput,
  Checkbox,
  Alert,
  Tooltip,
  useToastNotification,
} from '@enfomentis/ui';
import { sessionDraftService } from '@/services/SessionDraftService';
import { preTradingSettingsService } from '@/services/PreTradingSettingsService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { PreTradingConfigSnapshot } from '@/types/transient/SessionDraft';
import type { PreTradingSettings } from '@/types/schema/PreTradingSettings';

// ============================================================================
// Types
// ============================================================================

export interface ObjectivesWorkflowConfigModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Session draft ID */
  draftId: string;
  /** Current draft config snapshot */
  draftConfig: PreTradingConfigSnapshot;
  /** Current global settings (should be fresh when modal opens) */
  currentSettings: PreTradingSettings;
  /** Callback on successful save with updated maxObjectives */
  onSaveSuccess: (updatedMaxObjectives: number) => void;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Compare draft objectives config against current settings to detect divergence.
 * Used to determine checkbox default and "Reset to defaults" visibility.
 *
 * Note: Only compares maxObjectives since pinObjectivesToLive is excluded from
 * PreTradingConfigSnapshot (it's a UX preference that doesn't affect captured data).
 */
function isObjectivesConfigDivergent(
  draftConfig: PreTradingConfigSnapshot,
  currentSettings: PreTradingSettings
): boolean {
  return draftConfig.maxObjectives !== currentSettings.maxObjectives;
}

// ============================================================================
// Component
// ============================================================================

export const ObjectivesWorkflowConfigModal: React.FC<ObjectivesWorkflowConfigModalProps> = ({
  isOpen,
  onClose,
  draftId,
  draftConfig,
  currentSettings,
  onSaveSuccess,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToastNotification();

  // Local editing state
  const [localMaxObjectives, setLocalMaxObjectives] = useState(1);
  const [applyToFuture, setApplyToFuture] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Validation state
  const [validationError, setValidationError] = useState<string | null>(null);

  // Track whether draft was divergent from global when modal opened (snapshot, not live)
  const [wasInitiallyDivergent, setWasInitiallyDivergent] = useState(false);

  // Track previous isOpen state to detect open transition (false → true)
  const wasOpenRef = useRef(false);

  // Initialize local state when modal opens
  // Guard ensures we only initialize on open transition, not when props change while open
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setLocalMaxObjectives(draftConfig.maxObjectives);
      setValidationError(null);
      setIsSaving(false);

      // Compute initial divergence from draftConfig vs currentSettings
      const initiallyDivergent = isObjectivesConfigDivergent(draftConfig, currentSettings);
      setWasInitiallyDivergent(initiallyDivergent);

      // Checkbox default: checked if draft matched global at open, unchecked if divergent
      setApplyToFuture(!initiallyDivergent);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, draftConfig, currentSettings]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validateMaxObjectives = useCallback((value: number): string | null => {
    if (value < 1) {
      return 'Must be at least 1';
    }
    if (!Number.isInteger(value)) {
      return 'Must be a whole number';
    }
    return null;
  }, []);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleMaxObjectivesChange = useCallback(
    (value: number | undefined) => {
      const numValue = value ?? 0;
      setLocalMaxObjectives(numValue);
      setValidationError(validateMaxObjectives(numValue));
    },
    [validateMaxObjectives]
  );

  const handleNavigateToLibrary = useCallback(() => {
    // Close modal first, then navigate
    onClose();
    navigate('/settings/workflow/objectives');
  }, [onClose, navigate]);

  const handleResetToDefaults = useCallback(() => {
    setLocalMaxObjectives(currentSettings.maxObjectives);
    setValidationError(null);
    // Set checkbox to checked after reset (user explicitly chose to match defaults)
    setApplyToFuture(true);
    // Clear divergence warning since user explicitly chose to match defaults
    setWasInitiallyDivergent(false);
  }, [currentSettings]);

  // ============================================================================
  // Save Handler
  // ============================================================================

  const handleSave = useCallback(async () => {
    // Guard: currentSettings must be available
    if (!currentSettings) {
      showToast({
        title: 'Cannot save',
        description: 'Settings are not available. Please try again.',
        color: 'danger',
      });
      return;
    }

    // Validate
    const error = validateMaxObjectives(localMaxObjectives);
    if (error) {
      setValidationError(error);
      showToast({
        title: 'Validation error',
        description: 'Please fix the errors before saving.',
        color: 'warning',
      });
      return;
    }

    setIsSaving(true);

    try {
      // Step 1: Update draft config (maxObjectives only)
      const draftSuccess = await sessionDraftService.updateObjectivesConfig(
        draftId,
        localMaxObjectives
      );

      if (!draftSuccess) {
        showToast({
          title: 'Save failed',
          description: 'Session draft not found or invalid.',
          color: 'danger',
        });
        setIsSaving(false);
        return;
      }

      // Step 2: Update global settings (only if "Apply to future sessions" is checked)
      let globalUpdateSucceeded = true;
      if (applyToFuture) {
        try {
          await preTradingSettingsService.update(currentSettings.id, {
            maxObjectives: localMaxObjectives,
          });
          // Notify cross-tab sync
          syncCoordinatorService.notifyChange('pre_trading_settings', currentSettings.id);
        } catch (err) {
          console.error('Failed to update global settings:', err);
          globalUpdateSucceeded = false;
          showToast({
            title: 'Partial save',
            description:
              "Changes saved to current session, but couldn't update defaults for future sessions.",
            color: 'warning',
          });
        }
      }

      // Step 3: Check for global update failure (if applyToFuture was selected)
      // Note: Objectives (maxObjectives) are excluded from divergence tracking, so no ack hash logic
      if (applyToFuture && !globalUpdateSucceeded) {
        setIsSaving(false);
        return; // Keep modal open for retry
      }

      // Step 4: Notify parent of success with new value
      onSaveSuccess(localMaxObjectives);

      // Close modal on success
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      showToast({
        title: 'Save failed',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    currentSettings,
    localMaxObjectives,
    applyToFuture,
    draftId,
    validateMaxObjectives,
    showToast,
    onSaveSuccess,
    onClose,
  ]);

  // Check if there are unsaved changes
  const hasChanges = localMaxObjectives !== draftConfig.maxObjectives;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="top"
      scrollBehavior="inside"
      isDismissable={!isSaving}
      hideCloseButton={isSaving}
    >
      <ModalContent>
        <ModalHeader>
          <span className="text-lg font-semibold">Objectives Settings</span>
        </ModalHeader>
        <ModalBody className="gap-6">
          {/* Divergent State Warning - shows if draft was divergent when modal opened */}
          {wasInitiallyDivergent && (
            <Alert
              color="warning"
              title="This draft differs from your defaults"
              description="Your current edits won't affect this warning."
              endContent={
                <Button
                  size="sm"
                  variant="flat"
                  color="warning"
                  onPress={handleResetToDefaults}
                >
                  Reset to defaults
                </Button>
              }
            />
          )}

          {/* Max Objectives Input */}
          <NumberInput
            label="Maximum Objectives Per Session"
            description="How many objectives can be selected for each trading session"
            value={localMaxObjectives}
            onValueChange={handleMaxObjectivesChange}
            min={1}
            isInvalid={!!validationError}
            errorMessage={validationError}
            classNames={{ input: 'text-base' }}
          />

          {/* Divider */}
          <div className="border-t border-default-200" />

          {/* Navigate to Objectives Library */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium">Objectives Library</p>
              <p className="text-sm text-default-500">
                Create and manage your trading objectives
              </p>
            </div>
            <Tooltip
              content="Save or cancel changes first"
              isDisabled={!hasChanges}
            >
              <div>
                <Link
                  onPress={handleNavigateToLibrary}
                  isDisabled={hasChanges}
                  showAnchorIcon
                  size="sm"
                  className="cursor-pointer data-[disabled=true]:cursor-not-allowed"
                >
                  Manage Objectives
                </Link>
              </div>
            </Tooltip>
          </div>

          {/* Apply to Future Sessions Checkbox - always visible */}
          <Checkbox
            isSelected={applyToFuture}
            onValueChange={setApplyToFuture}
            size="sm"
          >
            Apply to future sessions
          </Checkbox>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button color="primary" onPress={handleSave} isLoading={isSaving}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ObjectivesWorkflowConfigModal;
